import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from ".";

export interface AccessRecord {
  id: string;
  types: string[];
  granted_to_user: {
    id: string;
    name: string;
    phone: string;
  };
  permissions: {
    viewMentor: boolean;
    viewMonthlyUpdates: boolean;
    viewSelfAssessmentData: boolean;
  };
}

export interface CreateAccessRecordData {
  firstName: string;
  lastName?: string;
  phone: string;
  types: string[];
  permissions: AccessRecord["permissions"];
}

export const useTeenAccessRecords = (menteeId?: string) => {
  return useQuery({
    queryKey: ["teen-access-records", menteeId],
    queryFn: async () => {
      const response = await fetchApi<{ records: AccessRecord[] }>({
        url: `/v2/teen-access/records/${menteeId}`,
        method: "GET",
      });

      if (!response.data) {
        throw new Error("No data returned from API");
      }

      return response.data.records;
    },
    enabled: !!menteeId,
  });
};

export const useUpdateTeenAccess = (menteeId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      permissions,
    }: {
      userId: string;
      permissions: AccessRecord["permissions"];
    }) => {
      const { data } = await fetchApi<AccessRecord>({
        url: `/v2/teen-access/permissions/${userId}/${menteeId}`,
        method: "PATCH",
        data: {
          permissions,
        },
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["teen-access-records", menteeId],
      });
    },
  });
};

export const useRevokeTeenAccess = (menteeId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await fetchApi<AccessRecord>({
        url: `/v2/teen-access/${userId}/${menteeId}`,
        method: "DELETE",
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["teen-access-records", menteeId],
      });
    },
  });
};

export const useCreateTeenAccess = (menteeId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAccessRecordData) => {
      const response = await fetchApi<AccessRecord>({
        url: `/v2/teen-access/grant/${menteeId}`,
        method: "POST",
        data,
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["teen-access-records", menteeId],
      });
    },
  });
};
